import React, { useEffect, useState } from 'react';
import { Footer } from '../components/Footer';
import { ExperiencesSection } from '../components/ExperiencesSection';
import { CategoriesSection } from '../components/CategoriesSection';
import { SEOHead } from '../components/SEOHead';
import { useLanguage } from '../i18n/LanguageContext';
import { BACKEND_URL } from '../lib/apiBase';
import { rewriteUploadsInHtml } from '../lib/uploadsUrl';

/**
 * Public Sitges Guide page — renders the rich HTML authored from the admin
 * panel. Entire layout is driven by the editor, so all styling choices
 * (headings, colors, images, alignment) come from the stored HTML.
 */
export function SitgesGuidePage() {
  const { lang, t } = useLanguage();
  const [html, setHtml] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [experiences, setExperiences] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Scroll to top only on initial mount, NOT on language switches so the
    // reader stays where they were when they change languages mid-page.
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/site-content/sitges_guide?lang=${lang}`);
        const data = await res.json();
        setHtml(rewriteUploadsInHtml(data.html || ''));
      } catch (err) {
        console.error('Failed to load Sitges Guide content', err);
        setHtml('');
      } finally {
        setLoaded(true);
      }
    })();
  }, [lang]);

  // Fetch experiences + categories once for the bottom-of-page rows
  // (Top Experiences carousel + Explore by Category tile grid).
  useEffect(() => {
    (async () => {
      try {
        const [expRes, catRes] = await Promise.all([
          // No per-language projection — all 12 translations come baked in
          // so language switches stay client-side and never flash to EN.
          fetch(`${BACKEND_URL}/api/experiences`),
          fetch(`${BACKEND_URL}/api/categories`),
        ]);
        const [expData, catData] = await Promise.all([expRes.json(), catRes.json()]);
        setExperiences(expData.experiences || []);
        setCategories(catData.categories || []);
      } catch (err) {
        console.error('Failed to load homepage rows for Sitges Guide:', err);
      }
    })();
  }, []);

  return (
    <>
      <SEOHead
        title="Sitges Guide — Local Insider Tips, Beaches, Restaurants & More | SitgesVibes"
        description="Your complete insider's guide to Sitges: beaches, restaurants, nightlife, festivals and Catalan culture — written by locals who live here."
      />
      <main
        className="max-w-[1280px] mx-auto px-5 sm:px-8 pt-[200px] lg:pt-[130px] pb-20"
        data-testid="sitges-guide-page"
        style={{ fontFamily: "'Outfit', sans-serif", color: '#2d5f8d' }}
      >
        {!loaded ? (
          <div className="flex items-center justify-center py-20 text-[#6b7a8c] text-sm">
            {t('loading')}
          </div>
        ) : html && html.trim() !== '' ? (
          <article
            className="sitges-guide-content prose prose-lg max-w-none
                       prose-headings:text-[#1a4d7a] prose-headings:font-bold
                       prose-p:text-[#2d5f8d] prose-li:text-[#2d5f8d]
                       prose-a:text-[#0184ff] prose-strong:text-[#1a4d7a]
                       prose-img:rounded-xl"
            data-testid="sitges-guide-content"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <div className="py-16 text-center">
            <h1 className="text-3xl font-bold mb-2" style={{ color: '#1a4d7a' }}>
              {t('sitges_guide_static_title')}
            </h1>
            <p className="text-base" style={{ color: '#2d5f8d' }}>
              {t('sitges_guide_coming_soon')}
            </p>
          </div>
        )}
      </main>
      {/* Bottom-of-guide rows: Top Experiences + Explore by Category. */}
      {experiences.length > 0 && <ExperiencesSection experiences={experiences} />}
      {categories.length > 0 && <CategoriesSection categories={categories} />}
      <Footer />
    </>
  );
}

export default SitgesGuidePage;
